import { ChangeEvent, useState } from 'react';
import { endOfDay, isAfter, isBefore } from 'date-fns';
import { useLang } from 'hooks/lang';

import { actions, User, isOwner, getTaskStatusConfig, projectStatusByCode } from 'entities';

import {
  Collapse,
  DatePicker,
  Row,
  LabeledSelect,
  IconButton,
  LabeledInput,
  Column,
  Label,
  Card,
} from 'components/Blocks';
import { getUserLabel } from 'components/Inputs';
import { PlusIcon, XIcon, CheckIcon } from 'components/Icons';

import { SubTaskList } from './List';

import { useTaskModal } from '../../context.taskModal';
import { useToggleV2, useUser } from 'hooks';
import { useTeam } from 'providers/Team';

import { getLocalDateString } from 'utils/date';
import { wrapFormAction } from 'utils/html';

type SubTaskFormData = {
  expectedDate: Date;
  owner: User;
  name: string;
};

export function TaskSubTasks() {
  const { t } = useLang();
  const loggedUser = useUser();

  const { teamUsers } = useTeam();

  const { task, project, updateTask } = useTaskModal();

  const [isCreating, creatingHandlers] = useToggleV2();

  const defaultSub = {
    owner: loggedUser,
    expectedDate: new Date(),
    name: '',
  } as unknown as SubTaskFormData;

  const [subTask, setSubTask] = useState(defaultSub);

  const handleTaskChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    const { id, value } = target;

    setSubTask((old) => ({
      ...old,
      [id]: value,
    }));
  };

  const shouldDisableDate = (date: Date | null): boolean => {
    if (!date) return false;

    const taskEnd = endOfDay(new Date(task.expectedDate as string));

    return isBefore(endOfDay(taskEnd), date);
  };

  const badCreationDate = shouldDisableDate(subTask.expectedDate);
  const canCreate = [Object.values(subTask).every(Boolean), !badCreationDate].every(Boolean);

  const create = actions.projects.createSubTask.use({
    block: !canCreate,

    params: {
      ...subTask,
      expectedDate: getLocalDateString(subTask.expectedDate),
      owner: subTask.owner.id,
      task: task.id,
      project: task.projectId,
    },

    onSuccess: (sub) => {
      updateTask((old) => ({
        ...old,
        subTasks: [...old.subTasks, sub],
      }));

      creatingHandlers.turnOff();
      setSubTask(defaultSub);
    },
  });

  const statusConfig = getTaskStatusConfig(task.status);
  const projectConfig = projectStatusByCode[project.status];

  const isBlocked = [
    statusConfig.blocks.fullBlock,
    statusConfig.blocks.addSubtasks,
    projectConfig.blocks.fullBlock,
  ].some(Boolean);

  const canAddSub =
    isOwner(task, loggedUser) && !isBlocked && isAfter(new Date(task.expectedDate!), new Date());

  if (!task.subTasks.length && !canAddSub) return null;

  return (
    <Card flow="column">
      <Row gap="2" justifyContent="space-between">
        <span className="font-medium">{t('subtasks')}</span>

        {canAddSub &&
          (isCreating ? (
            <Row gap="2" className="animate-in fade-in slide-in-from-right-4">
              <IconButton
                size="small"
                onClick={creatingHandlers.turnOff}
                disabled={create.loading}
                tooltipContent={t('cancel')}
              >
                <XIcon color="destructive" size={16} />
              </IconButton>

              <IconButton
                size="small"
                loadingSize={16}
                disabled={!canCreate}
                {...create.buttonProps}
                tooltipContent={
                  <Column gap="1" alignItems="center">
                    <span>{t('create-sub-task')}</span>
                    <span className="text-sm">{`(Press ↵)`}</span>
                  </Column>
                }
              >
                <CheckIcon color="success" size={16} />
              </IconButton>
            </Row>
          ) : (
            <IconButton size="small" onClick={creatingHandlers.turnOn} tooltipContent={t('create')}>
              <PlusIcon color="primary" size={16} />
            </IconButton>
          ))}
      </Row>

      <Collapse expanded={isCreating} onlyRenderIfExpanded>
        <Column
          as="form"
          className="grid grid-cols-3 phone:grid-cols-1"
          onSubmit={wrapFormAction(create.onClick)}
        >
          <button className="hidden" type="submit" />

          <LabeledInput
            id="name"
            label={t('name')}
            value={subTask.name || ''}
            onChange={handleTaskChange}
          />

          <LabeledSelect
            options={teamUsers}
            value={subTask.owner.id}
            label={t('owner')}
            labelKey="name"
            formatLabel={getUserLabel}
            onChangeDirect={(owner) =>
              setSubTask((old) => ({
                ...old,
                owner: teamUsers.find((user) => user.id === owner)!,
              }))
            }
          />

          <Column gap="2">
            <Label className={badCreationDate ? 'text-destructive' : ''}>
              {t('expected-date')}
            </Label>
            <DatePicker
              disabled={shouldDisableDate}
              disablePast
              selected={subTask.expectedDate}
              onSelect={(expectedDate) =>
                expectedDate &&
                setSubTask((old) => ({
                  ...old,
                  expectedDate,
                }))
              }
            />
          </Column>
        </Column>
      </Collapse>

      <SubTaskList />
    </Card>
  );
}
